/* Sign in, sign up, and the one dialog that does both.
 *
 * A native <dialog>, because showModal() brings a focus trap, Escape-to-close
 * and an inert background with it. Rebuilding those by hand on a plain div is
 * how the accessibility problems in #15 happened the first time.
 *
 * The form is deliberately dull. The interesting decisions are elsewhere: what
 * the server will actually accept (`authSettings`), and what happens to your
 * decks once you are in (#48). This is a door.
 */
import { h, fill } from './dom.js';
import { signIn, signUp, signInWithProvider, authSettings } from '../data/auth.js';

/* Supabase's own minimum is 6. Asking for 8 on a new account and nothing on an
 * existing one is the useful pair: stricter where it costs nothing, and never
 * standing between someone and an account they already have. */
const MIN_PASSWORD = 8;

export function createAuthDialog() {
  let mode = 'in';            // 'in' | 'up'
  let busy = false;
  let message = null;         // { kind: 'bad' | 'good', text }
  let settings = { google: false, discord: false, email: true, signupsOpen: true, confirmEmail: true };

  const email = h('input.input', {
    id: 'auth-email', type: 'email', autocomplete: 'email',
    placeholder: 'you@example.com', required: true,
  });

  const password = h('input.input', {
    id: 'auth-password', type: 'password', placeholder: '••••••••', required: true,
  });

  const body = h('div.authbody');
  const root = h('dialog.authdlg', {
    'aria-labelledby': 'auth-title',
    /* Clicking the backdrop lands on the dialog element itself, never on its
     * children. Anywhere else inside is a click on the form. */
    onclick: e => { if (e.target === root) close(); },
    /* Native Escape fires `cancel` then `close`; reset so the next open is not
     * still showing the last attempt's error. */
    onclose: () => { message = null; busy = false; },
  }, body);

  /* ------------------------------------------------------------------ */

  function say(kind, text) { message = { kind, text }; render(); }

  async function submit(e) {
    e?.preventDefault?.();
    if (busy) return;

    const address = email.value.trim();
    const secret = password.value;

    if (!address.includes('@')) return say('bad', 'That does not look like an email address.');
    if (!secret) return say('bad', 'Enter your password.');
    if (mode === 'up' && secret.length < MIN_PASSWORD) {
      return say('bad', `Passwords need at least ${MIN_PASSWORD} characters.`);
    }

    busy = true;
    message = null;
    render();

    const res = mode === 'up' ? await signUp(address, secret) : await signIn(address, secret);
    busy = false;

    if (!res.ok) return say('bad', res.error);

    if (res.needsConfirmation) {
      /* An unconfirmed signup returns no session, so the header would not
       * change and the dialog closing would read as "nothing happened". */
      password.value = '';
      return say('good', `Account created. Check ${address} for the confirmation link.`);
    }

    /* The header redraws off the auth event, not off this call. */
    close();
  }

  async function oauth(provider) {
    if (busy) return;
    busy = true;
    render();
    const res = await signInWithProvider(provider);
    /* On success the browser is already navigating away, so there is nothing
     * to re-render into. */
    if (!res.ok) { busy = false; say('bad', res.error); }
  }

  /* ------------------------------------------------------------------ */

  function render() {
    const up = mode === 'up';
    password.autocomplete = up ? 'new-password' : 'current-password';

    fill(body,
      /* `novalidate` because the browser's own bubble gets there first
       * otherwise: `type="email" required` cancels the submit event, so the
       * checks below never run and the user gets a native tooltip in a
       * different voice, different position and different wording per browser.
       * The `required` attributes stay for what they tell a screen reader. */
      h('form.authform', { novalidate: true, onsubmit: submit },
        h('h2#auth-title', up ? 'Create an account' : 'Sign in'),
        h('p.authsub',
          up
            ? 'Your decks stop living in one browser.'
            : 'Card Theory works signed out — an account is for keeping your decks.'),

        h('label.authlabel', { for: 'auth-email' }, 'Email'),
        email,

        h('label.authlabel', { for: 'auth-password' }, 'Password'),
        password,
        up && h('p.authhint', `At least ${MIN_PASSWORD} characters.`),

        message && h('p.authmsg', {
          class: message.kind === 'bad' ? 'is-bad' : 'is-good',
          role: 'alert',
        }, message.text),

        h('button.btn-pri.authsubmit', { type: 'submit', disabled: busy },
          busy ? 'Working…' : (up ? 'Create account' : 'Sign in')),

        /* Shown on the sign-up branch only. This is the moment an account —
         * and therefore an email address on our server — comes into existence,
         * so it is the moment the terms have to be in front of somebody rather
         * than a footer away. `target="_blank"` because losing a half-filled
         * form to read a policy is how people give up on reading it. */
        up && h('p.authlegal',
          'By creating an account you agree to the ',
          h('a', { href: '/terms', target: '_blank', rel: 'noopener' }, 'Terms'),
          ' and the ',
          h('a', { href: '/privacy', target: '_blank', rel: 'noopener' }, 'Privacy Policy'),
          '.'),

        /* Only rendered once the server says the provider is live, so there is
         * never a button here that cannot work. Discord leads where both are
         * enabled: it is the one that carries a supporter role, so it is the
         * sign-in that does something extra rather than merely something
         * faster. */
        (settings.discord || settings.google) && h('div.divider-label.author', h('span', 'OR')),
        settings.discord && h('button.btn.authoauth', {
          type: 'button', disabled: busy, onclick: () => oauth('discord'),
        }, 'Continue with Discord'),
        settings.google && h('button.btn.authoauth', {
          type: 'button', disabled: busy, onclick: () => oauth('google'),
        }, 'Continue with Google'),

        h('div.authfoot',
          settings.signupsOpen && h('button.btn-ghost', {
            type: 'button',
            onclick: () => { mode = up ? 'in' : 'up'; message = null; render(); },
          }, up ? 'I already have an account' : 'Create an account'),
          h('div.spacer'),
          h('button.btn-ghost', { type: 'button', onclick: close }, 'Cancel'),
        ),
      ),
    );
  }

  function open(which = 'in') {
    mode = which;
    message = null;
    busy = false;
    render();
    root.showModal ? root.showModal() : root.setAttribute('open', '');
    email.focus();

    /* Asked every open rather than once: enabling a provider is a dashboard click
     * with no deploy behind it, so a session that started before the switch
     * should still pick it up on the next try. `authSettings` caches the
     * answer, so this is one request per page, not per open.
     *
     * Re-render only when the answer changes something visible. `render()`
     * rebuilds every button in the dialog, and a click landing on one that was
     * swapped a frame earlier does nothing at all — the user sees a dialog
     * that ignored them once, with no error and nothing to retry against. The
     * cache is warmed during boot, so on the common path this resolves in a
     * microtask with the same answer and touches nothing. */
    authSettings().then(s => {
      if (s.google === settings.google && s.discord === settings.discord
        && s.signupsOpen === settings.signupsOpen) return;
      settings = s;

      /* Put the caret back. `render()` calls `fill()`, which re-appends the
       * persistent `email`/`password` inputs — and moving an element in the
       * DOM drops focus, silently. Until Discord was switched on nothing ever
       * changed here, so this path had never actually run: the dialog opened
       * focused, and stayed that way because the re-render never happened.
       *
       * The first thing it broke on going live was typing your email. */
      const active = document.activeElement;
      render();
      (active === password ? password : email).focus();
    });
  }

  function close() {
    root.close ? root.close() : root.removeAttribute('open');
    message = null;
    busy = false;
  }

  render();
  return { root, open, close, isOpen: () => !!root.open };
}
